import Phaser from 'phaser';
import { computeTooltipPlacement, type AnchorRect } from './tooltipPlacement.js';

export type { AnchorRect } from './tooltipPlacement.js';

export interface TooltipOptions {
  /** Max card width before wrapping the body. */
  width: number;
}

const PADDING_X = 12;
const PADDING_Y = 10;
const TITLE_BODY_GAP = 6;
const ANCHOR_GAP = 6;
const VIEWPORT_PAD = 4;
const CARD_BG_TOP = 0x0d2c1d;
const CARD_BG_BOT = 0x06140e;
const GOLD = 0xd4af37;

/**
 * Inline tooltip card. Visual style mirrors ZoneHelpCard so the language
 * stays consistent. Layout math lives in computeTooltipPlacement (pure,
 * tested separately). No hover wiring lives here.
 */
export class Tooltip extends Phaser.GameObjects.Container {
  private readonly bg: Phaser.GameObjects.Graphics;
  private readonly titleText: Phaser.GameObjects.Text;
  private readonly bodyText: Phaser.GameObjects.Text;
  private readonly maxWidth: number;
  private readonly dpr: number;

  constructor(scene: Phaser.Scene, opts: TooltipOptions) {
    super(scene, 0, 0);
    this.dpr = (scene.game.registry.get('dpr') as number | undefined) ?? 1;
    this.maxWidth = opts.width;

    this.bg = scene.add.graphics();
    this.add(this.bg);

    const innerWidth = this.maxWidth - this.px(PADDING_X) * 2;
    this.titleText = scene.add.text(0, 0, '', {
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: `${this.px(13)}px`,
      color: '#f4d35e',
      fontStyle: 'bold',
      wordWrap: { width: innerWidth },
    }).setOrigin(0, 0);
    this.add(this.titleText);

    this.bodyText = scene.add.text(0, 0, '', {
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: `${this.px(12)}px`,
      color: '#e6e6dc',
      wordWrap: { width: innerWidth },
    }).setOrigin(0, 0);
    this.add(this.bodyText);

    this.setVisible(false);
    scene.add.existing(this);
  }

  show(title: string, body: string, anchor: AnchorRect): void {
    this.titleText.setText(title);
    this.bodyText.setText(body);
    this.layoutAt(anchor);
    this.setVisible(true);
  }

  hide(): void {
    this.setVisible(false);
  }

  private px(n: number): number {
    return Math.round(n * this.dpr);
  }

  private layoutAt(anchor: AnchorRect): void {
    const padX = this.px(PADDING_X);
    const padY = this.px(PADDING_Y);
    const gap = this.px(TITLE_BODY_GAP);

    const innerWidth = this.maxWidth - padX * 2;
    this.titleText.setWordWrapWidth(innerWidth);
    this.bodyText.setWordWrapWidth(innerWidth);

    const titleH = this.titleText.height;
    const bodyH = this.bodyText.height;
    const cardW = this.maxWidth;
    const cardH = padY + titleH + (bodyH > 0 ? gap + bodyH : 0) + padY;

    const placement = computeTooltipPlacement({
      anchor,
      contentSize: { w: cardW, h: cardH },
      viewport: { w: this.scene.scale.width, h: this.scene.scale.height },
      vpad: this.px(VIEWPORT_PAD),
      anchorGap: this.px(ANCHOR_GAP),
    });

    this.bg.clear();
    this.bg.fillGradientStyle(CARD_BG_TOP, CARD_BG_TOP, CARD_BG_BOT, CARD_BG_BOT, 0.97);
    this.bg.fillRoundedRect(placement.x, placement.y, cardW, cardH, this.px(8));
    this.bg.lineStyle(this.px(1), GOLD, 1);
    this.bg.strokeRoundedRect(placement.x, placement.y, cardW, cardH, this.px(8));

    this.titleText.setPosition(placement.x + padX, placement.y + padY);
    this.bodyText.setPosition(placement.x + padX, placement.y + padY + titleH + gap);
  }
}
